// modelCatalogClient.js — renderer-side consumer of the server-driven catalog (E1-WS2-T03).
//
// Fetches GET /api/model-catalog once at boot, caches it (memory + localStorage), and resolves
// a provider's base URL from the SERVED catalog. SAFE FALLBACK (risk #4): on a failed fetch it
// keeps the last good cache; with no cache, resolveBaseUrl returns null so the caller falls back
// to its own hardcoded adapter catalog — the app is never left without models.
//
// Touches window/localStorage on purpose, so it is NOT a pure engine (excluded from
// arch:lib-purity, like sprintFlags.js).

import { apiFetch } from './api.js';

const LS_KEY = 'apolo_model_catalog_v1';
let mem = null; // { catalog, etag } in-memory cache for the session

function readLS() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLS(value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(value));
  } catch {
    // ignore — private mode / quota
  }
}

/** The cached catalog payload (memory → localStorage → null). */
export function getCachedCatalog() {
  if (mem) return mem;
  const ls = readLS();
  if (ls && Array.isArray(ls.catalog)) { mem = ls; return ls; }
  return null;
}

/**
 * Fetch + cache the served catalog. Best-effort: on any failure keeps the last good cache and
 * returns it (or null). Send If-None-Match so a 304 reuses the cache cheaply.
 * @returns {Promise<{catalog:Array, etag:string}|null>}
 */
export async function loadModelCatalog() {
  const cached = getCachedCatalog();
  try {
    const headers = cached?.etag ? { 'If-None-Match': cached.etag } : undefined;
    const res = await apiFetch('/api/model-catalog', { method: 'GET', headers });
    if (res?.status === 304 && cached) return cached;
    if (res?.ok && res.data && Array.isArray(res.data.catalog)) {
      mem = { catalog: res.data.catalog, etag: res.data.etag || '' };
      writeLS(mem);
      return mem;
    }
  } catch {
    // network/auth error → fall through to cache
  }
  return cached;
}

/**
 * Resolve a provider's base URL from the served catalog (specific model_id, else wildcard).
 * Returns null when there is no cached catalog OR the provider isn't in it — the caller then
 * uses its own hardcoded default (the fallback contract).
 * @param {string} provider
 * @param {string} [modelId]
 * @returns {string|null}
 */
export function resolveBaseUrl(provider, modelId = '*') {
  const cached = getCachedCatalog();
  const rows = cached?.catalog;
  if (!Array.isArray(rows)) return null;
  const specific = rows.find((r) => r.provider === provider && r.model_id === modelId);
  const wildcard = rows.find((r) => r.provider === provider && r.model_id === '*');
  return (specific || wildcard)?.base_url ?? null;
}

/** Test/sign-out helper — clear the in-memory cache. */
export function _resetCatalogCache() { mem = null; }
